import type TriggerEngine from "../../TriggerEngine";
import type { EventSourceResult, TriggerTimerState } from "../../TriggerEngine.interfaces";
import type { ITriggerRule } from "../../../interfaces/Widgets";
import { ruleIdentity } from "../ruleIdentity";
import { parseTimerSeconds } from "../timing";

interface TimerEntry {
  rule: ITriggerRule;
  identity: string;
  duration: number;
  remaining: number;
  deadline: number | null;
  generation: number;
}

export const wireTimerEvents = (engine: TriggerEngine): EventSourceResult => {
  const timers = new Map<string, ReturnType<typeof setTimeout>>();
  const entries = new Map<string, TimerEntry>();
  let disposed = false;
  let streaming = engine.Radio.Streaming.isStreaming();
  let streamGeneration = 0;

  const getState = (ruleId: string): TriggerTimerState | null => {
    const entry = entries.get(ruleId);
    if (!entry) return null;
    return {
      remainingSeconds: Math.max(0, entry.deadline === null
        ? entry.remaining : entry.deadline - Date.now()) / 1000,
      running: entry.deadline !== null,
    };
  };
  const notify = (ruleId: string) => {
    engine.emitAction("timer_state_changed", { ruleId, state: getState(ruleId) });
  };
  const cancel = (entry: TimerEntry) => {
    entry.generation++;
    clearTimeout(timers.get(entry.rule.id));
    timers.delete(entry.rule.id);
  };
  const scheduleTick = (entry: TimerEntry) => {
    const generation = entry.generation;
    const ruleId = entry.rule.id;
    const remaining = Math.max(0, entry.deadline! - Date.now());
    timers.set(ruleId, setTimeout(() => {
      if (disposed || entries.get(ruleId) !== entry || entry.generation !== generation) return;
      timers.delete(ruleId);
      entry.remaining = Math.max(0, entry.deadline! - Date.now());
      if (entry.remaining > 0) {
        notify(ruleId);
        scheduleTick(entry);
        return;
      }
      entry.deadline = null;
      notify(ruleId);
      void engine.fireRule(entry.rule, () => !disposed && entries.get(ruleId) === entry &&
        entry.generation === generation);
    }, Math.min(1000, remaining)));
  };
  const start = (ruleId: string) => {
    const entry = entries.get(ruleId);
    if (!entry || disposed || entry.deadline !== null) return;
    cancel(entry);
    if (entry.remaining <= 0) entry.remaining = entry.duration;
    entry.deadline = Date.now() + entry.remaining;
    scheduleTick(entry);
    notify(ruleId);
  };
  const pause = (ruleId: string) => {
    const entry = entries.get(ruleId);
    if (!entry) return;
    cancel(entry);
    if (entry.deadline !== null) entry.remaining = Math.max(0, entry.deadline - Date.now());
    entry.deadline = null;
    notify(ruleId);
  };
  const reset = (ruleId: string) => {
    const entry = entries.get(ruleId);
    if (!entry) return;
    cancel(entry);
    entry.remaining = entry.duration;
    entry.deadline = null;
    notify(ruleId);
  };
  const sync = (rules: ITriggerRule[]) => {
    const valid = new Map(rules.filter((rule) => rule.enabled && rule.event === "timer_reached" &&
      parseTimerSeconds(rule.eventConfig?.seconds) !== null).map((rule) => [rule.id, rule]));
    for (const [ruleId, entry] of entries) {
      if (valid.has(ruleId)) continue;
      cancel(entry);
      entries.delete(ruleId);
      notify(ruleId);
    }
    for (const [ruleId, rule] of valid) {
      const existing = entries.get(ruleId);
      const identity = ruleIdentity(rule);
      if (existing?.identity === identity) {
        existing.rule = rule;
        notify(ruleId);
        continue;
      }
      if (existing) cancel(existing);
      const duration = parseTimerSeconds(rule.eventConfig.seconds)! * 1000;
      entries.set(ruleId, { rule, identity, duration, remaining: duration, deadline: null, generation: 0 });
      notify(ruleId);
      if (streaming) start(ruleId);
    }
  };
  const onStarted = () => {
    streaming = true;
    const generation = ++streamGeneration;
    void engine.sync().then(() => {
      if (disposed || !streaming || generation !== streamGeneration) return;
      for (const ruleId of entries.keys()) { reset(ruleId); start(ruleId); }
    }).catch((error) => { console.error("[TriggerEngine] Timer start failed:", error); });
  };
  const onStopped = () => {
    streaming = false;
    streamGeneration++;
    for (const ruleId of entries.keys()) reset(ruleId);
  };
  engine.Radio.Streaming.on("started", onStarted);
  engine.Radio.Streaming.on("stopped", onStopped);

  return {
    sync,
    timerControls: { getState, start, pause, reset },
    timers,
    unsubscribers: [() => {
      disposed = true;
      streamGeneration++;
      engine.Radio.Streaming.off("started", onStarted);
      engine.Radio.Streaming.off("stopped", onStopped);
      for (const [ruleId, entry] of entries) {
        cancel(entry);
        entries.delete(ruleId);
        notify(ruleId);
      }
    }],
  };
};
